// The shared instance, not bare axios: it attaches the X-CSRF-Token header from
// the page's meta tag on every request. Rails rejects an unverified mutation
// with 422 InvalidAuthenticityToken, so bare axios makes every GET work and
// every save fail — which is exactly how this page shipped until it was tried
// against a real server.
import axios from '../../../utils/axiosInstance';

// Every request to the GMPRO configuration endpoints lives here. Components take
// data and callbacks only — the epic's stated standard is that query logic stays
// out of them, and it is also what keeps the paths in one place when they move.
const base = '/admin/gmpro_runs/configuration';
const unwrap = (response) => response.data;

/**
 * Fetches the list of GMPRO configuration scopes.
 *
 * @returns {Promise<Array>} The scope list.
 */
export const fetchScopes = () => axios.get(`${base}.json`).then(unwrap);

/**
 * Fetches the configuration detail for one scope.
 *
 * @param {string|number} scope - The scope identifier — "global" or a collective id.
 * @returns {Promise<Object>} The scope's configuration detail.
 */
export const fetchScope = (scope) => axios.get(`${base}/${scope}.json`).then(unwrap);

/**
 * Saves the knobs for one scope. This is the deliberate save action for the
 * configuration form.
 *
 * @param {string|number} scope - The scope identifier — "global" or a collective id.
 * @param {Object} knobs - The knob values to persist.
 * @returns {Promise<Object>} The updated scope's configuration detail.
 */
export const saveScope = (scope, knobs, configurationDigest) =>
  axios.patch(`${base}/${scope}.json`, { knobs, configuration_digest: configurationDigest }).then(unwrap);

/**
 * Toggles whether a scope participates in GMPRO.
 *
 * @param {string|number} scope - The scope identifier — "global" or a collective id.
 * @param {boolean} enabled - Whether GMPRO should be enabled for the scope.
 * @returns {Promise<Object>} The updated participation state.
 */
export const setParticipation = (scope, enabled) =>
  axios.patch(`${base}/${scope}/participation.json`, { gmpro_enabled: enabled }).then(unwrap);

/**
 * Toggles whether a company is included in a scope's GMPRO membership.
 *
 * @param {string|number} scope - The scope identifier — "global" or a collective id.
 * @param {number} companyId - The company's id.
 * @param {boolean} included - Whether the company should be included.
 * @returns {Promise<Object>} The updated member state.
 */
export const setMember = (scope, companyId, included) =>
  axios.patch(`${base}/${scope}/members/${companyId}.json`, { gmpro_included: included }).then(unwrap);

/**
 * Adds a company as a member of a scope.
 *
 * @param {string|number} scope - The scope identifier — "global" or a collective id.
 * @param {number} companyId - The company's id.
 * @returns {Promise<Object>} The created member record.
 */
export const addMember = (scope, companyId) =>
  axios.post(`${base}/${scope}/members.json`, { company_id: companyId }).then(unwrap);

/**
 * Removes a company from a scope's membership.
 *
 * @param {string|number} scope - The scope identifier — "global" or a collective id.
 * @param {number} companyId - The company's id.
 * @returns {Promise<Object>} The response payload confirming removal.
 */
export const removeMember = (scope, companyId) =>
  axios.delete(`${base}/${scope}/members/${companyId}.json`).then(unwrap);
